import React, { useContext, useEffect, useMemo } from "react";
import AgGridLayout from "@wrappers/layouts/AgGridLayout";
import { numericValueFormatter } from "@ts/utils";
import { customFilterOptions } from "@ts/constants";
import {
    ChangeInValueCellRenderer,
    TargetCellRenderer,
} from "@cell-renderers/AllCellRenderers";
import { mainContext } from "@ts/constants";
import { onPortfolioSelectionChanged } from "@ts/performance-overview";
import { useSelector } from "react-redux";
import AddRulesModal from "@modals/rules-modal/AddRulesModal";
import { IMainContext, ParentModule, Portfolio, SubModule } from "@ts/common-types";
import { useGetModulesData, useUpdateModule } from "@hooks/AllCustomHooks";

interface IPortfolio {
    portfoliosData: {
        value: Portfolio[];
    };
}

const Portfolios = React.memo(() => {
    const portfoliosData = useSelector(
        (state: IPortfolio) => state.portfoliosData.value
    );

    const { portfoliosLoading, portfoliosGridApi, setPortfoliosGridApi } =
        useContext<IMainContext>(mainContext);


    const gridParams = useGetModulesData({
        type: "grid-params",
        activeModuleName: SubModule.PORTFOLIO,
        activeParentModuleName: ParentModule.PERFORMANCE_OVERVIEW,
    });

    const portfoliosColumnDefs = useGetModulesData({
        type: "columns",
        colDefs: [
            {
                field: "portfolio_name",
                headerName: "Portfolio",
                filter: "agTextColumnFilter",
                cellStyle: { whiteSpace: "normal" },
                checkboxSelection: true,
                pinned: "left",
                lockPinned: true,
                cellClass: "lock-pinned",
                tooltipField: "campaign_name",
                cellRenderer: TargetCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    pinnedColumnValue: "Total",
                    origin: "portfolios",
                },
                minWidth: 330,
                maxWidth: 480,
                headerCheckboxSelection: true
            },
            {
                headerName: "Impressions",
                cellStyle: { whiteSpace: "normal" },
                field: "impression",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                valueFormatter: numericValueFormatter,
                cellRenderer: ChangeInValueCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    decimalPlace: 0,
                    showChangePercent: true,
                },
            },
            {
                headerName: "Clicks",
                cellStyle: { whiteSpace: "normal" },
                field: "click",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                    decimalPlace: 0,
                },
            },
            {
                headerName: "Spends",
                cellStyle: { whiteSpace: "normal" },
                field: "spend",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                sort: "desc",
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                headerName: "Orders",
                cellStyle: { whiteSpace: "normal" },
                field: "orders",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                    decimalPlace: 0,
                },
            },
            {
                headerName: "Sales",
                cellStyle: { whiteSpace: "normal" },
                field: "sale",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                headerName: "RoAS",
                cellStyle: { whiteSpace: "normal" },
                field: "roas",
                filter: "agNumberColumnFilter",
                valueFormatter: numericValueFormatter,
                cellRenderer: ChangeInValueCellRenderer,
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                headerName: "CTR",
                cellStyle: { whiteSpace: "normal" },
                field: "ctr",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: true,
                    showChangePercent: true,
                    decimalPlace: 1,
                },
            },
            {
                headerName: "ACoS",
                cellStyle: { whiteSpace: "normal" },
                field: "acos",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                hide: true,
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: true,
                    showChangePercent: true,
                },
            },
        ]
    });

    const updateModule = useUpdateModule("portfolios");

    useEffect(() => {
        sessionStorage.setItem("trendType", "portfolios");
    }, []);

    return (
        <>
            <div className="wrapper" style={{ flex: "1 1 0" }}>
                <div
                    id="portfolios-grid"
                    className="ag-theme-alpine"
                    style={{ height: "100%", width: "100%" }}
                >
                    <AgGridLayout
                        setGridApi={setPortfoliosGridApi}
                        columnDefs={portfoliosColumnDefs}
                        rowData={portfoliosData}
                        onSelectionChanged={onPortfolioSelectionChanged}
                        updateModule={updateModule}
                        loading={portfoliosLoading}
                        isTotalRowsReq={true}
                        rowHeight={gridParams?.rowHeight}
                    />
                </div>
                <AddRulesModal api={portfoliosGridApi} />
            </div>
        </>
    );
});

export default Portfolios;
